import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "../../context/AppContext";
import useComplianceLifecycle from "../../hooks/useComplianceLifecycle";
import ComplianceRoadmap from "../../components/common/ComplianceRoadmap";
import ComplianceRecommendationsPanel from "../../components/common/ComplianceRecommendationsPanel";
import {
  daysUntilDeadline,
  formatDeadlineDate,
  roadProgressScore,
  completedRoadPhases,
  ROAD_PHASES,
  timelineProgressPct,
  formatStartDate,
} from "../../data/complianceRoadmap";
import {
  BarChart2,
  RefreshCw,
  ChevronRight,
  Sparkles,
  Building2,
  Calendar,
  Route,
} from "lucide-react";

function getDisplayName(user) {
  return user?.full_name || user?.name || user?.email || "there";
}

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  }),
};

const GLASS = {
  background: "rgba(255,255,255,0.72)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.9)",
  boxShadow:
    "0 4px 24px rgba(37,99,235,0.08), 0 1px 3px rgba(15,23,42,0.04), inset 0 1px 0 rgba(255,255,255,1)",
};

function GlassSection({ children, className = "", delay = 0 }) {
  return (
    <motion.section
      custom={delay}
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className={`rounded-3xl overflow-visible border border-white/90 ${className}`}
      style={GLASS}
    >
      {children}
    </motion.section>
  );
}

export default function UserHome() {
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const {
    loading,
    refreshing,
    refresh,
    progress,
    complianceLabel,
    orgName,
    roadmapDeptProgress,
    activeUsers,
    organizationCreatedAt,
  } = useComplianceLifecycle();

  const displayName = getDisplayName(currentUser);
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const daysLeft = daysUntilDeadline();
  const roadScore = roadProgressScore(progress);
  const phasesDone = completedRoadPhases(progress);
  const totalPhases = ROAD_PHASES.length;
  const startDate =
    organizationCreatedAt || currentUser?.organization_created_at || null;
  const timelineProgress = timelineProgressPct(startDate);

  return (
    <div
      className="min-h-full -m-4 lg:-m-6 font-sans relative overflow-hidden bg-slate-50"
      style={{
        background:
          "linear-gradient(160deg, #f8fafc 0%, #eff6ff 30%, #eef2ff 55%, #f0f9ff 100%)",
      }}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 65%)",
          }}
          animate={{ x: [0, -30, 0], y: [0, 40, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 -left-40 w-[480px] h-[480px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(14,165,233,0.1) 0%, transparent 65%)",
          }}
          animate={{ x: [0, 35, 0], y: [0, -25, 0] }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(148,163,184,0.15) 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
      </div>

      <div className="relative z-10">
        <motion.header
          data-tour-id="tour-user-welcome"
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="px-5 sm:px-8 py-4 sm:py-5 border-b border-slate-200/60"
          style={{
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(20px)",
          }}
        >
          <div className="flex items-center justify-between gap-3 min-w-0">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0 shrink">
              <motion.div
                whileHover={{ rotateY: 12, scale: 1.05 }}
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: "linear-gradient(135deg, #6366f1, #0ea5e9)",
                  boxShadow:
                    "0 8px 24px rgba(99,102,241,0.3), inset 0 1px 0 rgba(255,255,255,0.35)",
                }}
              >
                <Sparkles size={20} className="text-white" />
              </motion.div>
              <div className="min-w-0">
                <h1
                  data-tour-id="tour-user-greeting"
                  title={`${greeting}, ${displayName}`}
                  className="text-xl sm:text-2xl font-bold text-slate-900 font-display tracking-tight leading-tight whitespace-nowrap truncate cursor-default"
                >
                  {greeting}, {displayName}
                </h1>
                <p className="text-sm text-slate-600 mt-1 flex items-center gap-2 whitespace-nowrap min-w-0">
                  <Building2
                    size={14}
                    className="text-indigo-500 flex-shrink-0"
                  />
                  <span data-tour-id="tour-user-org" className="truncate">
                    {orgName}
                  </span>
                  <span className="text-slate-300 flex-shrink-0">·</span>
                  <span
                    data-tour-id="tour-user-compliance-label"
                    className="text-primary-600 font-medium truncate"
                  >
                    {complianceLabel || "Building readiness"}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-nowrap shrink-0">
              <div
                data-tour-id="tour-user-start-date"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/80 border border-slate-200/80 shadow-sm shrink-0 whitespace-nowrap"
              >
                <Calendar size={14} className="text-emerald-600 shrink-0" />
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wide text-slate-600 leading-none">
                    Start Date
                  </p>
                  <p className="text-xs font-bold text-slate-900 tabular-nums">
                    {formatStartDate(startDate)}
                  </p>
                </div>
              </div>
              <div
                data-tour-id="tour-user-timeline-progress"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/80 border border-slate-200/80 shadow-sm shrink-0 whitespace-nowrap"
              >
                <Route size={14} className="text-violet-600 shrink-0" />
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wide text-slate-600 leading-none">
                    Progress
                  </p>
                  <p className="text-xs font-bold text-slate-900 tabular-nums">
                    {timelineProgress}%
                  </p>
                </div>
              </div>
              <div
                data-tour-id="tour-user-deadline"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/80 border border-slate-200/80 shadow-sm shrink-0 whitespace-nowrap"
              >
                <Calendar size={14} className="text-amber-600 shrink-0" />
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wide text-slate-600 leading-none">
                    Deadline
                  </p>
                  <p className="text-xs font-bold text-slate-900 tabular-nums">
                    {daysLeft} days · {formatDeadlineDate()}
                  </p>
                </div>
              </div>
              <div
                data-tour-id="tour-user-road-progress"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/80 border border-slate-200/80 shadow-sm shrink-0 whitespace-nowrap"
              >
                <Route size={14} className="text-indigo-600 shrink-0" />
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wide text-slate-600 leading-none">
                    Road progress
                  </p>
                  <p className="text-xs font-bold text-slate-900 tabular-nums">
                    {loading
                      ? "…"
                      : `${phasesDone}/${totalPhases} phases · ${roadScore}%`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                data-tour-id="tour-user-refresh"
                onClick={refresh}
                disabled={refreshing}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-white/80 border border-slate-200/80 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md shrink-0 whitespace-nowrap"
              >
                <RefreshCw
                  size={13}
                  className={refreshing ? "animate-spin" : ""}
                />
                Refresh
              </button>
              <button
                type="button"
                data-tour-id="tour-user-dashboard-btn"
                onClick={() => navigate("/user/dashboard")}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg shrink-0 whitespace-nowrap"
                style={{
                  background: "linear-gradient(135deg, #6366f1, #0ea5e9)",
                  boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
                }}
              >
                <BarChart2 size={14} />
                Dashboard
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        </motion.header>

        <div className="px-4 sm:px-6 lg:px-8 py-4 space-y-4">
          <GlassSection delay={1}>
            <div data-tour-id="tour-user-roadmap">
              <ComplianceRoadmap
                progress={progress}
                loading={loading}
                complianceLabel={complianceLabel}
                deptProgress={roadmapDeptProgress}
                activeUsers={activeUsers}
                startDate={startDate}
                timelineProgress={timelineProgress}
                allowedModules={currentUser?.modules || []}
              />
            </div>
          </GlassSection>

          <GlassSection delay={2}>
            <div data-tour-id="tour-user-recommendations">
              <ComplianceRecommendationsPanel
                progress={progress}
                orgName={orgName}
                loading={loading}
              />
            </div>
          </GlassSection>
        </div>
      </div>
    </div>
  );
}
